import { useCallback, useEffect, useState } from 'react';
import { vendorService, type VendorComplianceCounts } from '../../../services/vendorService';

export interface UseVendorComplianceCountsResult {
  counts: VendorComplianceCounts | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * V2-078 – the Home Vendor Compliance tiles: Overdue, Due in 30 days, Missing documents. The
 * counts come from the same server query as the Vendor Compliance report, so a tile's number is
 * the report's row count for that status.
 *
 * `enabled` is driven by the permission at the call site, so a user without it never issues the
 * request.
 */
export function useVendorComplianceCounts(enabled: boolean): UseVendorComplianceCountsResult {
  const [counts, setCounts] = useState<VendorComplianceCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!enabled) {
      setCounts(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setCounts(await vendorService.getComplianceCounts());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load vendor compliance'));
      setCounts(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return { counts, loading, error, refetch: fetchCounts };
}
